/**
 * BulkModuleEnricher — the Module Enrichment tool, the electronic-module
 * counterpart to BulkCalibreEnricher. Mirrors that component's shape exactly:
 * scan a brand's references, read each ref's free-text legacy module value
 * (`electronics.module`, the admin `elecModule` field), and reconcile it against
 * the `electronicModules` collection.
 *
 *  - Top-level surface (catalogue → Module Enrichment): pick a brand to scan.
 *  - Brand-scoped surface (embedded, launched from a brand's Enrichment Tasks
 *    panel): the brand is locked via `initialBrandId` and the page header +
 *    brand picker are hidden.
 *
 * Per-ref outcomes (matches the calibre reconcile model; the extra "linked"
 * bucket is the deliberate difference — modules use a real `moduleId` FK link
 * rather than calibre's string-mirroring, so an already-linked ref is a distinct
 * resolved state):
 *   - ref already has a `moduleId`            → Linked (resolved; informational)
 *   - no `moduleId`, no `electronics.module`  → Missing (nothing to match on)
 *   - no `moduleId`, string matches a module  → Matched → "Link" (writes moduleId)
 *   - no `moduleId`, string has no match      → Unmatched → "✨ AI Lookup"
 *
 * Matching is a case-insensitive normalized lookup against every module's `name`
 * and `alsoKnownAs` aliases. Unlike calibres, modules carry no brand scope (they
 * are manufacturer-attributed), so the index spans the whole collection.
 *
 * The Unmatched "✨ AI Lookup" reuses ElectronicModuleAiLookup, which chains
 * `suggestElectronicModule` (grounded with the group's legacy `electronics.*`
 * values) → either link an existing match, or create a new module via the editor
 * with `autoEnrichOnSave` (auto-fires `enrichElectronicModule`) → then links the
 * whole group's refs to the resolved/created module.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import {
  db,
  getWatchBrands,
  getExistingReferenceMap,
  getElectronicModules,
  type SuggestElectronicModuleInput,
} from '../../services/firebase';
import type { ElectronicModule, WatchBrand, WatchReference } from '../../types';
import { useManufacturerCatalogue } from '../../hooks/useManufacturerCatalogue';
import { Button } from '../common/Button';
import { BrandFilterSelect } from './BrandFilterSelect';
import { ElectronicModuleAiLookup } from '../ElectronicModules/ElectronicModuleAiLookup';

const WATCH_BRANDS_COLLECTION = 'watchBrands';
const REFERENCES_SUBCOLLECTION = 'references';

type SectionKey = 'unmatched' | 'matched' | 'linked' | 'missing';

interface ModuleMatch {
  module: ElectronicModule;
  matchType: 'name' | 'alias';
  matchedOn: string;
}

interface MissingRow {
  refId: string;
  reference: string;
  model?: string;
}

/** A group of refs sharing one normalized `electronics.module` value. */
interface UnmatchedGroup {
  moduleValue: string;
  refs: WatchReference[];
}

interface MatchedGroup {
  moduleValue: string;
  refs: WatchReference[];
  matchedModule: ElectronicModule;
  matchType: 'name' | 'alias';
  /** The un-normalized name or alias the ref's module value matched against. */
  matchedOn: string;
}

/** Refs already linked to a module via `moduleId`, grouped by that module. */
interface LinkedGroup {
  moduleId: string;
  moduleName: string;
  refs: WatchReference[];
}

interface ScanResults {
  brandId: string;
  brandName: string;
  unmatched: UnmatchedGroup[];
  matched: MatchedGroup[];
  linked: LinkedGroup[];
  missing: MissingRow[];
}

function normalizeKey(s: string): string {
  return s.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Build a lookup index over every module: normalized name/alias → match info.
 *  Modules carry no brand scope, so this spans the whole collection. A canonical
 *  name always wins over an alias colliding on the same normalized key. */
function buildModuleIndex(modules: ElectronicModule[]): Map<string, ModuleMatch> {
  const idx = new Map<string, ModuleMatch>();
  for (const m of modules) {
    if (m.name && m.name.trim()) {
      idx.set(normalizeKey(m.name), { module: m, matchType: 'name', matchedOn: m.name });
    }
    for (const alias of m.alsoKnownAs || []) {
      if (!alias || !alias.trim()) continue;
      const key = normalizeKey(alias);
      // Don't override an existing name match for the same key with an alias.
      if (idx.has(key) && idx.get(key)?.matchType === 'name') continue;
      idx.set(key, { module: m, matchType: 'alias', matchedOn: alias });
    }
  }
  return idx;
}

/** First non-empty value a picker yields across a group's refs — used to ground
 *  the `suggestElectronicModule` call from the group's legacy inline values. */
function firstNonEmpty(
  refs: WatchReference[],
  pick: (r: WatchReference) => string | undefined,
): string | undefined {
  for (const r of refs) {
    const v = pick(r);
    if (v && v.trim()) return v.trim();
  }
  return undefined;
}

/** Link every ref in the group to `moduleId` (writes the FK; a Cloud Function
 *  trigger keeps each ref's `electronics` cache in sync). Returns in-memory
 *  copies carrying the new `moduleId` so the local cache stays accurate. */
async function linkRefsToModule(
  brandId: string,
  refs: WatchReference[],
  moduleId: string,
): Promise<WatchReference[]> {
  const updated: WatchReference[] = [];
  for (const ref of refs) {
    await updateDoc(
      doc(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION, ref.id),
      { moduleId, updatedAt: Timestamp.now() },
    );
    updated.push({ ...ref, moduleId });
  }
  return updated;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface BulkModuleEnricherProps {
  /** Pre-select + lock this brand (deep-link from a brand's Enrichment Tasks panel). */
  initialBrandId?: string;
  /** Called once the initial brand has been applied, so the parent can clear it. */
  onBrandConsumed?: () => void;
  /** When embedded in a brand's panel modal: hide the page header + brand picker. */
  embedded?: boolean;
}

export function BulkModuleEnricher({
  initialBrandId,
  onBrandConsumed,
  embedded = false,
}: BulkModuleEnricherProps = {}) {
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState<ScanResults | null>(null);
  const [expanded, setExpanded] = useState<Record<SectionKey, boolean>>({
    unmatched: true,
    matched: true,
    linked: false,
    missing: false,
  });
  const [linking, setLinking] = useState<Record<string, boolean>>({});
  const [bulkLinking, setBulkLinking] = useState(false);
  // Unmatched groups resolved this session via AI Lookup: normalized key →
  // linked module name. These stay in the Unmatched section (rather than moving
  // to Linked) so the AI Lookup's still-open create+enrich editor isn't
  // unmounted mid-review; a re-scan re-buckets them into Linked.
  const [resolvedUnmatched, setResolvedUnmatched] = useState<Record<string, string>>({});

  // Seed + lock the brand when deep-linked from the per-brand panel.
  useEffect(() => {
    if (initialBrandId) {
      setSelectedBrandId(initialBrandId);
      onBrandConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBrandId]);

  // Load brands once (for the picker + to resolve the brand name in embedded mode).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await getWatchBrands();
        if (!cancelled) setBrands(list.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        console.error('Failed to load brands', err);
        toast.error('Failed to load brands');
      } finally {
        if (!cancelled) setLoadingBrands(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Manufacturer display names for the results tables. Names only, so this comes
  // from `watchkeeper_manufacturers` rather than reading all 176
  // `movement_manufacturers` documents — and through the shared hook, so a
  // manufacturer created by the module editor this screen opens is named rather
  // than shown as a slug.
  const { manufacturers: moduleManufacturers } = useManufacturerCatalogue();
  const manufacturerNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const m of moduleManufacturers) map[m.id] = m.displayName || m.name;
    return map;
  }, [moduleManufacturers]);

  const selectedBrand = useMemo(
    () => brands.find((b) => b.id === selectedBrandId),
    [brands, selectedBrandId],
  );

  const resetScanState = useCallback(() => {
    setResults(null);
    setLinking({});
    setBulkLinking(false);
    setResolvedUnmatched({});
  }, []);

  const handleBrandChange = (brandId: string) => {
    setSelectedBrandId(brandId);
    resetScanState();
  };

  const handleScan = useCallback(async () => {
    if (!selectedBrand) {
      toast.error('Select a brand first');
      return;
    }
    setScanning(true);
    resetScanState();
    try {
      const [refMap, modules] = await Promise.all([
        getExistingReferenceMap(selectedBrand.id),
        getElectronicModules(true),
      ]);
      const refs = Array.from(refMap.values()).sort((a, b) =>
        (a.reference || '').localeCompare(b.reference || ''),
      );
      const index = buildModuleIndex(modules);
      const moduleById = new Map(modules.map((m) => [m.id, m]));

      const missing: MissingRow[] = [];
      const matchedMap = new Map<string, MatchedGroup>();
      const unmatchedMap = new Map<string, UnmatchedGroup>();
      const linkedMap = new Map<string, LinkedGroup>();

      for (const ref of refs) {
        const mid = (ref.moduleId || '').trim();
        const raw = (ref.electronics?.module || '').trim();

        // Already linked → resolved. Show for transparency regardless of string.
        if (mid) {
          const existing = linkedMap.get(mid);
          if (existing) {
            existing.refs.push(ref);
          } else {
            linkedMap.set(mid, {
              moduleId: mid,
              moduleName: moduleById.get(mid)?.name || raw || mid,
              refs: [ref],
            });
          }
          continue;
        }

        // Unlinked with no module value → nothing to match on.
        if (!raw) {
          missing.push({ refId: ref.id, reference: ref.reference, model: ref.model });
          continue;
        }

        const key = normalizeKey(raw);
        const hit = index.get(key);
        if (hit) {
          const existing = matchedMap.get(key);
          if (existing) {
            existing.refs.push(ref);
          } else {
            matchedMap.set(key, {
              moduleValue: raw,
              refs: [ref],
              matchedModule: hit.module,
              matchType: hit.matchType,
              matchedOn: hit.matchedOn,
            });
          }
        } else {
          const existing = unmatchedMap.get(key);
          if (existing) {
            existing.refs.push(ref);
          } else {
            unmatchedMap.set(key, { moduleValue: raw, refs: [ref] });
          }
        }
      }

      const sortByRefCount = <T extends { refs: unknown[] }>(g: T[]): T[] =>
        g.sort((a, b) => b.refs.length - a.refs.length);

      setResults({
        brandId: selectedBrand.id,
        brandName: selectedBrand.name,
        unmatched: sortByRefCount(Array.from(unmatchedMap.values())),
        matched: sortByRefCount(Array.from(matchedMap.values())),
        linked: sortByRefCount(Array.from(linkedMap.values())),
        missing,
      });

      const matchedRefCount = Array.from(matchedMap.values()).reduce(
        (sum, g) => sum + g.refs.length,
        0,
      );
      toast.success(
        `Scanned ${refs.length} refs — ${matchedMap.size} matched groups (${matchedRefCount} refs), ` +
          `${unmatchedMap.size} unmatched groups, ${linkedMap.size} already linked, ${missing.length} missing`,
      );
    } catch (err) {
      console.error('Module scan failed', err);
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  }, [selectedBrand, resetScanState]);

  /** Merge a just-linked group's refs into the Linked section (by moduleId). */
  const addToLinked = useCallback(
    (prev: ScanResults, moduleId: string, moduleName: string, refs: WatchReference[]): LinkedGroup[] => {
      const existing = prev.linked.find((g) => g.moduleId === moduleId);
      const nextLinked = existing
        ? prev.linked.map((g) =>
            g.moduleId === moduleId ? { ...g, refs: [...g.refs, ...refs] } : g,
          )
        : [...prev.linked, { moduleId, moduleName, refs }];
      return nextLinked.sort((a, b) => b.refs.length - a.refs.length);
    },
    [],
  );

  /** Link a matched group's refs to its matched module, then move it to Linked. */
  const handleLinkMatched = useCallback(
    async (group: MatchedGroup) => {
      if (!results) return;
      const key = normalizeKey(group.moduleValue);
      setLinking((p) => ({ ...p, [key]: true }));
      try {
        const updated = await linkRefsToModule(
          results.brandId,
          group.refs,
          group.matchedModule.id,
        );
        setResults((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            matched: prev.matched.filter((g) => normalizeKey(g.moduleValue) !== key),
            linked: addToLinked(prev, group.matchedModule.id, group.matchedModule.name, updated),
          };
        });
        toast.success(
          `Linked ${updated.length} ref${updated.length === 1 ? '' : 's'} to "${group.matchedModule.name}"`,
        );
      } catch (err) {
        console.error('Link failed for', group.moduleValue, err);
        toast.error(err instanceof Error ? err.message : 'Link failed');
      } finally {
        setLinking((p) => {
          const next = { ...p };
          delete next[key];
          return next;
        });
      }
    },
    [results, addToLinked],
  );

  /** Bulk-link every matched group to its matched module. */
  const handleLinkAllMatched = useCallback(async () => {
    if (!results || results.matched.length === 0) return;
    const refTotal = results.matched.reduce((s, g) => s + g.refs.length, 0);
    if (
      !window.confirm(
        `Link ${refTotal} reference${refTotal === 1 ? '' : 's'} across ${results.matched.length} matched ` +
          `group${results.matched.length === 1 ? '' : 's'} to their matched modules?`,
      )
    ) {
      return;
    }
    setBulkLinking(true);
    let linkedRefs = 0;
    let failed = 0;
    const groups = [...results.matched];
    for (const group of groups) {
      const key = normalizeKey(group.moduleValue);
      try {
        const updated = await linkRefsToModule(
          results.brandId,
          group.refs,
          group.matchedModule.id,
        );
        linkedRefs += updated.length;
        setResults((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            matched: prev.matched.filter((g) => normalizeKey(g.moduleValue) !== key),
            linked: addToLinked(prev, group.matchedModule.id, group.matchedModule.name, updated),
          };
        });
      } catch (err) {
        console.error('Bulk link failed for', group.moduleValue, err);
        failed++;
      }
    }
    setBulkLinking(false);
    if (failed === 0) {
      toast.success(`Linked ${linkedRefs} reference${linkedRefs === 1 ? '' : 's'}`);
    } else {
      toast.error(`Linked ${linkedRefs}; ${failed} group${failed === 1 ? '' : 's'} failed`);
    }
  }, [results, addToLinked]);

  /** Link an unmatched group after ElectronicModuleAiLookup resolved/created a
   *  module for it (match → link, or create → auto-enrich → link). The group
   *  stays in the Unmatched section marked "✓ Linked" (rather than moving to
   *  Linked) so the lookup's still-open create+enrich editor isn't unmounted
   *  mid-review — a re-scan re-buckets it into Linked. */
  const handleLinkUnmatched = useCallback(
    async (group: UnmatchedGroup, moduleId: string, moduleName: string) => {
      if (!results) return;
      const key = normalizeKey(group.moduleValue);
      try {
        const updated = await linkRefsToModule(results.brandId, group.refs, moduleId);
        setResolvedUnmatched((prev) => ({ ...prev, [key]: moduleName || moduleId }));
        toast.success(
          `Linked ${updated.length} ref${updated.length === 1 ? '' : 's'} to "${moduleName || moduleId}"`,
        );
      } catch (err) {
        console.error('Link (from AI lookup) failed for', group.moduleValue, err);
        toast.error(err instanceof Error ? err.message : 'Link failed');
      }
    },
    [results],
  );

  /** Build the `suggestElectronicModule` input for an unmatched group, grounded
   *  with the group's legacy inline `electronics.*` values + brand context. */
  const buildSuggestInput = useCallback(
    (group: UnmatchedGroup): SuggestElectronicModuleInput => ({
      brand: selectedBrand?.name || results?.brandName || undefined,
      productName: firstNonEmpty(group.refs, (r) => r.model),
      legacyValues: {
        module: group.moduleValue,
        display: firstNonEmpty(group.refs, (r) => r.electronics?.display),
        battery: firstNonEmpty(group.refs, (r) => r.electronics?.battery),
        lightType: firstNonEmpty(group.refs, (r) => r.electronics?.lightType),
        lcdType: firstNonEmpty(group.refs, (r) => r.electronics?.lcdType),
      },
    }),
    [selectedBrand, results],
  );

  const toggleSection = (key: SectionKey) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const manufacturerLabel = useCallback(
    (m: ElectronicModule): string => {
      if (!m.manufacturerId) return '—';
      return manufacturerNames[m.manufacturerId] || m.manufacturerId;
    },
    [manufacturerNames],
  );

  const matchedRefTotal = useMemo(
    () => results?.matched.reduce((sum, g) => sum + g.refs.length, 0) ?? 0,
    [results],
  );
  const linkedRefTotal = useMemo(
    () => results?.linked.reduce((sum, g) => sum + g.refs.length, 0) ?? 0,
    [results],
  );

  return (
    <div className="space-y-6">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Module Enrichment</h1>
          <p className="text-gray-500 mt-1">
            Scan a brand's references and reconcile their legacy{' '}
            <code className="font-mono text-sm">electronics.module</code> values against the{' '}
            <code className="font-mono text-sm">electronicModules</code> database. Link refs
            to matching modules, or use AI Lookup to create + enrich a new module and link
            it back.
          </p>
        </div>
      )}

      {/* Brand picker (hidden when brand is locked) */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
        {!embedded && (
          <BrandFilterSelect
            brands={brands}
            loading={loadingBrands}
            value={selectedBrandId}
            onChange={handleBrandChange}
          />
        )}
        <div>
          <Button onClick={handleScan} disabled={!selectedBrandId || scanning} loading={scanning}>
            Scan references
          </Button>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{results.brandName}</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                <span className="text-amber-700 font-medium">
                  {results.unmatched.length} unmatched groups
                </span>
                {' · '}
                <span className="text-blue-700 font-medium">
                  {results.matched.length} matched ({matchedRefTotal} refs)
                </span>
                {' · '}
                <span className="text-green-700 font-medium">
                  {results.linked.length} linked ({linkedRefTotal} refs)
                </span>
                {' · '}
                <span className="text-gray-600 font-medium">{results.missing.length} missing</span>
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={handleLinkAllMatched}
                disabled={results.matched.length === 0 || bulkLinking}
                loading={bulkLinking}
                variant="success"
              >
                Link All Matched ({results.matched.length} group
                {results.matched.length === 1 ? '' : 's'}, {matchedRefTotal} ref
                {matchedRefTotal === 1 ? '' : 's'})
              </Button>
            </div>
          </div>

          {/* Unmatched */}
          <Section
            title="Unmatched"
            count={results.unmatched.length}
            expanded={expanded.unmatched}
            onToggle={() => toggleSection('unmatched')}
            tone="amber"
          >
            {results.unmatched.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 italic">Nothing unmatched.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-amber-50 text-xs text-amber-900 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Module Value</th>
                    <th className="px-3 py-2 text-left">References</th>
                    <th className="px-3 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {results.unmatched.map((group) => {
                    const key = normalizeKey(group.moduleValue);
                    const resolvedName = resolvedUnmatched[key];
                    return (
                      <tr key={key} className="bg-amber-50/30">
                        <td className="px-3 py-2 align-top">
                          <div className="font-mono text-sm font-medium text-gray-900">
                            {group.moduleValue}
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <RefsDisclosure refs={group.refs} />
                        </td>
                        <td className="px-3 py-2 align-top">
                          {/* Keep the lookup mounted even after linking so its
                              still-open create+enrich editor survives. */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <ElectronicModuleAiLookup
                              buildInput={() => buildSuggestInput(group)}
                              onLink={(moduleId, moduleName) =>
                                void handleLinkUnmatched(group, moduleId, moduleName)
                              }
                            />
                            {resolvedName && (
                              <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800 font-medium">
                                ✓ Linked to {resolvedName}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Section>

          {/* Matched */}
          <Section
            title="Matched (ready to link)"
            count={results.matched.length}
            expanded={expanded.matched}
            onToggle={() => toggleSection('matched')}
            tone="blue"
          >
            {results.matched.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 italic">Nothing matched.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-blue-50 text-xs text-blue-900 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Module Value</th>
                    <th className="px-3 py-2 text-left">Matched To</th>
                    <th className="px-3 py-2 text-left">How</th>
                    <th className="px-3 py-2 text-left">References</th>
                    <th className="px-3 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {results.matched.map((group) => {
                    const key = normalizeKey(group.moduleValue);
                    const isLinking = !!linking[key];
                    return (
                      <tr key={key} className="bg-blue-50/20">
                        <td className="px-3 py-2 align-top font-mono text-xs">
                          {group.moduleValue}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <span className="text-blue-800 font-medium">
                            {group.matchedModule.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {' '}
                            ({manufacturerLabel(group.matchedModule)})
                          </span>
                        </td>
                        <td className="px-3 py-2 align-top">
                          {group.matchType === 'name' ? (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800 font-medium">
                              name
                            </span>
                          ) : (
                            <span className="inline-flex items-baseline gap-1">
                              <span className="px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-800 font-medium">
                                alias
                              </span>
                              <span className="font-mono text-xs text-gray-600">
                                "{group.matchedOn}"
                              </span>
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <RefsDisclosure refs={group.refs} />
                        </td>
                        <td className="px-3 py-2 align-top">
                          <Button
                            size="sm"
                            onClick={() => handleLinkMatched(group)}
                            disabled={isLinking || bulkLinking}
                            loading={isLinking}
                          >
                            Link ({group.refs.length})
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Section>

          {/* Linked */}
          <Section
            title="Linked (resolved)"
            count={results.linked.length}
            expanded={expanded.linked}
            onToggle={() => toggleSection('linked')}
            tone="green"
          >
            {results.linked.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 italic">No references linked yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-green-50 text-xs text-green-900 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Linked Module</th>
                    <th className="px-3 py-2 text-left">References</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {results.linked.map((group) => (
                    <tr key={group.moduleId} className="bg-green-50/20">
                      <td className="px-3 py-2 align-top">
                        <span className="text-green-800 font-medium">{group.moduleName}</span>
                        <span className="ml-1 font-mono text-xs text-gray-400">
                          {group.moduleId}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <RefsDisclosure refs={group.refs} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* Missing */}
          <Section
            title="Missing (no module value)"
            count={results.missing.length}
            expanded={expanded.missing}
            onToggle={() => toggleSection('missing')}
            tone="gray"
          >
            {results.missing.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 italic">
                All unlinked references have a module value.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Reference</th>
                    <th className="px-3 py-2 text-left">Model</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {results.missing.map((r) => (
                    <tr key={r.refId}>
                      <td className="px-3 py-2 font-mono text-xs">{r.reference}</td>
                      <td className="px-3 py-2">{r.model || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
        </div>
      )}
    </div>
  );
}

function RefsDisclosure({ refs }: { refs: WatchReference[] }) {
  return (
    <details>
      <summary className="text-xs text-gray-700 cursor-pointer">
        {refs.length} reference{refs.length === 1 ? '' : 's'}
      </summary>
      <ul className="mt-1 text-xs text-gray-600 max-h-40 overflow-y-auto">
        {refs.slice(0, 50).map((r) => (
          <li key={r.id} className="font-mono">
            {r.reference}
            {r.model && <span className="font-sans text-gray-500"> — {r.model}</span>}
          </li>
        ))}
        {refs.length > 50 && (
          <li className="italic text-gray-400">…and {refs.length - 50} more</li>
        )}
      </ul>
    </details>
  );
}

function Section({
  title,
  count,
  expanded,
  onToggle,
  tone,
  children,
}: {
  title: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  tone: 'amber' | 'blue' | 'green' | 'gray';
  children: React.ReactNode;
}) {
  const toneClass = {
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    green: 'bg-green-50 border-green-200 text-green-900',
    gray: 'bg-gray-50 border-gray-200 text-gray-800',
  }[tone];
  return (
    <div className="border-t border-gray-200">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full px-4 py-3 flex items-center justify-between text-sm font-medium ${toneClass} border-l-4`}
      >
        <span>
          {title} <span className="ml-1 font-normal">({count})</span>
        </span>
        <span className="text-xs">{expanded ? '▲' : '▼'}</span>
      </button>
      {expanded && <div className="overflow-x-auto">{children}</div>}
    </div>
  );
}
